import { useState, useEffect } from "react";
import { adminApi } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { Building2, Users, FileText, TrendingUp, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const response = await adminApi.getStats();
      setStats(response.data);
    } catch (error) {
      toast.error("Error al cargar estadísticas");
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN'
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#5B9BD5]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="admin-dashboard">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Panel de Administración</h1>
        <p className="text-slate-600">Vista general de CIA Factura℗</p>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="metric-card">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
              <Building2 className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <p className="metric-label">Empresas</p>
              <p className="metric-value">{stats?.total_companies || 0}</p>
            </div>
          </div>
        </Card>

        <Card className="metric-card">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center">
              <Users className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <p className="metric-label">Usuarios</p>
              <p className="metric-value">{stats?.total_users || 0}</p>
            </div>
          </div>
        </Card>

        <Card className="metric-card">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center">
              <FileText className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <p className="metric-label">Total Facturas</p>
              <p className="metric-value">{stats?.total_invoices || 0}</p>
            </div>
          </div>
        </Card>

        <Card className="metric-card">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-amber-600" />
            </div>
            <div>
              <p className="metric-label">Suscripciones Activas</p>
              <p className="metric-value">{stats?.subscriptions?.active || 0}</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Subscription breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="cia-card">
          <CardHeader>
            <CardTitle className="text-lg">Estado de Suscripciones</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-3 h-3 rounded-full bg-green-500"></div>
                  <span className="font-medium text-slate-700">Activas</span>
                </div>
                <span className="text-2xl font-bold text-green-600">{stats?.subscriptions?.active || 0}</span>
              </div>
              <div className="flex items-center justify-between p-4 bg-amber-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-3 h-3 rounded-full bg-amber-500"></div>
                  <span className="font-medium text-slate-700">En Prueba</span>
                </div>
                <span className="text-2xl font-bold text-amber-600">{stats?.subscriptions?.trial || 0}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="cia-card">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg">Empresas Recientes</CardTitle>
            <Link to="/admin/companies" className="text-[#5B9BD5] text-sm font-medium hover:underline flex items-center gap-1">
              Ver todas <ArrowRight className="w-4 h-4" />
            </Link>
          </CardHeader>
          <CardContent>
            {stats?.recent_companies?.length === 0 ? (
              <p className="text-slate-500 text-center py-4">No hay empresas registradas</p>
            ) : (
              <div className="space-y-3">
                {stats?.recent_companies?.map((company) => (
                  <div key={company.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div>
                      <p className="font-medium text-slate-900">{company.name}</p>
                      <p className="text-sm text-slate-500">RFC: {company.rfc}</p>
                    </div>
                    <span className={company.subscription_status === 'active' ? 'badge-paid' : 'badge-pending'}>
                      {company.subscription_status === 'active' ? 'Activa' : 'Prueba'}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recent invoices */}
      <Card className="cia-card">
        <CardHeader>
          <CardTitle className="text-lg">Facturas Recientes (Todas las empresas)</CardTitle>
        </CardHeader>
        <CardContent>
          {stats?.recent_invoices?.length === 0 ? (
            <p className="text-slate-500 text-center py-8">No hay facturas aún</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Folio</th>
                    <th>Cliente</th>
                    <th>Total</th>
                    <th>Estado</th>
                    <th>Fecha</th>
                  </tr>
                </thead>
                <tbody>
                  {stats?.recent_invoices?.map((invoice) => (
                    <tr key={invoice.id}>
                      <td className="font-medium">{invoice.series}-{invoice.folio}</td>
                      <td>{invoice.client_name}</td>
                      <td>{formatCurrency(invoice.total)}</td>
                      <td>
                        <span className={`badge-${invoice.status}`}>
                          {invoice.status === 'draft' ? 'Borrador' : invoice.status === 'issued' ? 'Timbrada' : 'Cancelada'}
                        </span>
                      </td>
                      <td className="text-slate-500">
                        {new Date(invoice.created_at).toLocaleDateString('es-MX')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminDashboard;
